import { useEffect, useRef, type ReactNode } from 'react'
import clsx from 'clsx'

import { sourcePing } from '../../lib/motion'
import { ValueSwap } from '../motion/ValueSwap'
import type { Tone } from './model'

// ============================================================================
// The small repeated pieces of the Connections screen: section rules, status
// pills, stat pairs, and the one button component every action on the screen
// goes through.
// ============================================================================

/** `SOURCES ────────────` — a 600 10px 0.08em label and a hairline to the edge. */
export function SectionRule({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-[10px]">
      <h2 className="text-[10px] font-semibold uppercase leading-none tracking-[0.08em] text-microcopy">
        {label}
      </h2>
      <div className="h-px flex-1 bg-line" aria-hidden />
    </div>
  )
}

export function Section({
  label,
  children,
  className,
}: {
  label: string
  children: ReactNode
  className?: string
}) {
  return (
    <section className={clsx('flex flex-col gap-[11px]', className)}>
      <SectionRule label={label} />
      {children}
    </section>
  )
}

// ── Status pills ────────────────────────────────────────────────────────────

const PILL_TONE: Record<Tone | 'success', { wrap: string; dot: string; text: string }> = {
  success: { wrap: 'bg-success-fill', dot: 'bg-success-dot', text: 'text-success' },
  danger: { wrap: 'bg-danger-fill', dot: 'bg-danger-dot', text: 'text-danger-2' },
  warning: { wrap: 'bg-warning-fill', dot: 'bg-warning-dot', text: 'text-warning-2' },
  neutral: { wrap: 'bg-fill', dot: 'bg-muted', text: 'text-muted' },
}

/**
 * `● ERROR` / `● OK` / `● RATE LIMITED` / `ONLINE` / `OFFLINE`.
 *
 * The dot is not decoration — it is what makes the state readable at a glance
 * before the word is read, and it is the same 5px dot the rail and the settings
 * nav use, one size down. `PLANNED · V2` is the one pill with no dot: there is
 * no state to indicate, because there is nothing running.
 */
export function StatusPill({
  tone,
  label,
  dot = true,
  pingKey,
}: {
  tone: Tone | 'success'
  label: string
  dot?: boolean
  /**
   * 13c's source dot. Change this and the dot pings ONCE — a single expanding ring that fades
   * out, never a loop. A looping ring is a spinner in a different shape, and this fires because
   * something already arrived, not because anything is being waited for.
   *
   * Nothing pings on first paint: a value that is there because the screen just mounted is not
   * news.
   */
  pingKey?: string
}) {
  const c = PILL_TONE[tone]
  const dotNode = useRef<HTMLSpanElement>(null)
  const seen = useRef(pingKey)

  useEffect(() => {
    if (pingKey === undefined || seen.current === pingKey) return
    seen.current = pingKey
    sourcePing(dotNode.current)
  }, [pingKey])

  return (
    <span
      className={clsx(
        'flex flex-none items-center gap-[5px] rounded-pill px-2 py-[2px] text-[9.5px] font-semibold leading-[1.7]',
        c.wrap,
        c.text,
      )}
    >
      {dot && <span ref={dotNode} className={clsx('size-[5px] rounded-full', c.dot)} aria-hidden />}
      {label}
    </span>
  )
}

// ── Stat pairs ──────────────────────────────────────────────────────────────

/** `LAST SYNC` over `3 days ago · Aug 21, 6:12pm`. */
export function StatPair({
  label,
  value,
  emphatic,
}: {
  label: string
  value: string
  /** The queued-jobs count while the PC is asleep: the number that is the point. */
  emphatic?: boolean
}) {
  return (
    <div className="flex flex-none flex-col gap-[3px]">
      <div className="text-[9.5px] font-semibold uppercase leading-none tracking-[0.06em] text-microcopy">
        {label}
      </div>
      {/* 13c · SYNC, WITHOUT A SPINNER. When a sync brings a new value back, the stale one lifts
          out and the fresh one rises in. When it brings the SAME value back, nothing moves at
          all — and that stillness is the information: it is how the owner sees which stat the
          connector actually refreshed. `ValueSwap` keys off the value itself, so no caller has to
          remember which pairs to leave alone. */}
      <ValueSwap
        value={value}
        className={clsx(
          'text-[11.5px] leading-none',
          emphatic ? 'font-semibold text-warning-2' : 'font-medium text-ink-2',
        )}
      >
        {value}
      </ValueSwap>
    </div>
  )
}

// ── The one button ──────────────────────────────────────────────────────────

export type ActionVariant = 'outline' | 'quiet' | 'dark' | 'danger' | 'warning' | 'inert'

/**
 * Base fill and its hover step, kept SEPARATE (not one class string) so the button below can drop
 * the hover half entirely for a button that is not `live` — §2's "disabled controls have no
 * hover, no cursor change, no transition. Not dimmed-and-still-reactive — inert" applies just as
 * much to a designed-but-unbuilt action (`aria-disabled`, no `onClick`) as to a native `disabled`
 * element, and a control that lit up under the pointer while refusing the click it promised was
 * exactly the bug the owner reported (see the block comment above).
 */
const VARIANT: Record<ActionVariant, { base: string; hover: string }> = {
  outline: { base: 'bg-surface border border-line text-ink-2', hover: 'hover:bg-surface-2' },
  quiet: { base: 'bg-surface border border-line text-ink-3', hover: 'hover:bg-surface-2' },
  dark: { base: 'bg-ink text-surface font-semibold', hover: 'hover:bg-ink-2' },
  danger: { base: 'bg-danger-solid text-on-dark font-semibold', hover: 'hover:bg-danger-solid-2' },
  warning: { base: 'bg-warning-solid text-on-dark font-semibold', hover: 'hover:bg-warning-solid-2' },
  inert: { base: 'bg-fill-2 text-microcopy', hover: '' },
}

/**
 * SOME ACTIONS ON THIS SCREEN ARE HONEST ABOUT BEING UNBUILT.
 *
 * Several of these buttons have no backend yet: the job queue arrives in V1, capture tokens in
 * V5, the index in V4. A button that silently does nothing is worse than no button — so each
 * renders as designed, is focusable and reachable by keyboard, and carries `aria-disabled` plus
 * the sentence naming what it is waiting for. Clicking does nothing and claims nothing.
 *
 * THAT TREATMENT IS FOR ACTIONS NOBODY COULD TAKE YET, NEVER FOR RECOVERY. The owner reported
 * exactly this failure: a red `Reconnect` on a broken connector that did nothing on click and
 * explained itself only in a hover `title`, which reads as a broken app rather than as a design.
 * An action offered where something is WRONG has to be live, or it must not be offered at all —
 * so the error state's `Try again` (card and page banner both) is wired to the real sync.
 *
 * `inert` is different in kind: `Retrying…` and `Not available yet` are inert BY
 * DESIGN — a scheduled retry needs no button and a planned connector is not
 * clickable on purpose — so they carry no promise of a later version.
 *
 * `onClick` is what makes a button live (`Sync now`, `Try again`): passing it flips the button
 * from inert-by-design to real — no `aria-disabled`, a real click, and `busy` for the request in
 * flight — and `waitingFor` no longer applies, because there is nothing left to wait for.
 */
export function ActionButton({
  label,
  variant = 'outline',
  size = 'sm',
  waitingFor,
  icon,
  className,
  onClick,
  busy = false,
}: {
  label: string
  variant?: ActionVariant
  /** `sm` is the in-card 11px action; `md` is the 11.5px header action. */
  size?: 'sm' | 'md' | 'xs'
  /** What has to exist before this can work. Omitted only for `inert`. Ignored once `onClick` is wired. */
  waitingFor?: string
  icon?: ReactNode
  className?: string
  /** Present only on the handful of buttons that are actually wired to something real. */
  onClick?: () => void
  /** True while the wired action is in flight. No effect without `onClick`. */
  busy?: boolean
}) {
  const inert = variant === 'inert'
  const live = onClick !== undefined
  return (
    <button
      type="button"
      aria-disabled={live ? undefined : 'true'}
      disabled={live && busy}
      title={live ? undefined : waitingFor}
      onClick={live ? onClick : (e) => e.preventDefault()}
      className={clsx(
        'flex flex-none items-center gap-[7px] whitespace-nowrap font-medium leading-none',
        // Inert is inert: a button with no `onClick` gets no colour transition either, or the
        // hover step it no longer has would still visibly animate in and out on `:active`.
        live && 'transition-colors duration-[var(--dur-hover)] ease-[var(--ease-ink)]',
        size === 'xs'
          ? 'rounded-button-sm px-[10px] py-[5px] text-[10.5px]'
          : 'rounded-button px-3 py-[6px]',
        size === 'md' ? 'text-[11.5px]' : size === 'sm' ? 'text-[11px]' : '',
        inert ? 'cursor-default' : live ? (busy ? 'cursor-wait' : 'cursor-pointer') : 'cursor-not-allowed',
        live && busy ? 'opacity-70' : '',
        VARIANT[variant].base,
        live && VARIANT[variant].hover,
        className,
      )}
    >
      {icon}
      {label}
    </button>
  )
}

// ── Honest absence ──────────────────────────────────────────────────────────

/**
 * What a panel shows when its read threw. The screen whose subject is honest
 * degradation does not get to degrade dishonestly: an empty connector list would
 * read as "you have connected nothing", which is a claim, and a spinner forever
 * would read as "still loading", which is a lie.
 */
export function PanelUnavailable({ error }: { error: unknown }) {
  const message =
    error instanceof Error
      ? error.message
      : 'This could not be read, and the reason did not come back with a sentence attached.'
  return (
    <div className="flex items-start gap-[9px] rounded-card border border-line bg-surface px-[17px] py-[15px]">
      <span className="mt-[2px] size-[5px] flex-none rounded-full bg-microcopy" aria-hidden />
      <p className="text-[11.5px] leading-[1.45] text-ink-3 text-pretty">{message}</p>
    </div>
  )
}

/** A skeleton with the geometry of the card it stands in for, never a spinner. */
export function PanelSkeleton({ height = 96 }: { height?: number }) {
  return (
    <div
      className="animate-pulse rounded-card border border-line bg-surface"
      style={{ height }}
      aria-hidden
    />
  )
}
